<template>
  <div class="dashboard">
    <!-- Welcome Section -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card
          class="welcome-card"
          color="primary"
          dark
          rounded="lg"
        >
          <v-card-text class="pa-6">
            <v-row align="center">
              <v-col cols="12" md="8">
                <h1 class="text-h4 font-weight-bold mb-2">
                  Welcome back, {{ user.first_name }}!
                </h1>
                <p class="text-h6 opacity-90 mb-0">
                  {{ getGreeting() }} Let's see what's happening with the board today.
                </p>
              </v-col>
              <v-col cols="12" md="4" class="text-center">
                <v-avatar size="120" class="elevation-4">
                  <v-img
                    v-if="user.profile_image"
                    :src="getFileUrl(user.profile_image)"
                    :alt="user.full_name"
                  />
                  <span v-else class="text-h2 font-weight-bold">
                    {{ user.first_name?.[0] }}{{ user.last_name?.[0] }}
                  </span>
                </v-avatar>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Quick Stats -->
    <v-row class="mb-6">
      <v-col
        v-for="stat in stats"
        :key="stat.title"
        cols="12"
        sm="6"
        md="3"
      >
        <v-card
          class="stat-card"
          :color="stat.color"
          variant="outlined"
          rounded="lg"
          height="120"
        >
          <v-card-text class="pa-4 d-flex align-center">
            <div class="flex-grow-1">
              <p class="text-caption text-medium-emphasis mb-1">
                {{ stat.title }}
              </p>
              <h2 class="text-h4 font-weight-bold">
                <span v-if="stat.loading">
                  <v-skeleton-loader type="text" width="60px" />
                </span>
                <span v-else>{{ stat.value }}</span>
              </h2>
              <p v-if="stat.change && !stat.loading" class="text-caption mb-0" :class="stat.changeColor">
                <v-icon v-if="stat.changeIcon" size="small" :icon="stat.changeIcon" />
                {{ stat.change }}
              </p>
            </div>
            <v-avatar
              :color="stat.color"
              variant="tonal"
              size="56"
            >
              <v-icon :icon="stat.icon" size="28" />
            </v-avatar>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <!-- Upcoming Meetings -->
      <v-col cols="12" lg="6">
        <v-card rounded="lg" class="h-100">
          <v-card-title class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="primary">mdi-calendar-clock</v-icon>
              Upcoming Meetings
            </div>
            <v-btn
              variant="text"
              color="primary"
              size="small"
              to="/meetings"
            >
              View All
            </v-btn>
          </v-card-title>
          
          <v-card-text>
            <div v-if="upcomingMeetings.length === 0" class="text-center py-8">
              <v-icon size="64" color="grey-lighten-2">mdi-calendar-blank</v-icon>
              <p class="text-h6 text-medium-emphasis mt-4">No upcoming meetings</p>
              <v-btn color="primary" to="/meetings/create">
                Schedule Meeting
              </v-btn>
            </div>
            
            <v-list v-else density="compact">
              <v-list-item
                v-for="meeting in upcomingMeetings"
                :key="meeting.id"
                :to="`/meetings/${meeting.id}`"
                rounded="lg"
                class="mb-2"
              >
                <template #prepend>
                  <v-avatar
                    :color="meeting.meeting_type?.color || 'primary'"
                    size="40"
                    variant="tonal"
                  >
                    <v-icon>mdi-calendar</v-icon>
                  </v-avatar>
                </template>
                
                <v-list-item-title class="font-weight-medium">
                  {{ meeting.title }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ formatDateTime(meeting.start_datetime) }}
                  <br>
                  <v-chip
                    size="x-small"
                    :color="meeting.meeting_type?.color || 'primary'"
                    variant="outlined"
                  >
                    {{ meeting.meeting_type?.name }}
                  </v-chip>
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Pending Actions -->
      <v-col cols="12" lg="6">
        <v-card rounded="lg" class="h-100">
          <v-card-title class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="warning">mdi-bell-alert</v-icon>
              Pending Actions
            </div>
            <v-btn
              variant="text"
              color="primary"
              size="small"
              to="/resolutions"
            >
              View All
            </v-btn>
          </v-card-title>
          
          <v-card-text>
            <div v-if="pendingActions.length === 0" class="text-center py-8">
              <v-icon size="64" color="grey-lighten-2">mdi-check-all</v-icon>
              <p class="text-h6 text-medium-emphasis mt-4">All caught up!</p>
              <p class="text-body-2 text-medium-emphasis">No pending actions at the moment.</p>
            </div>
            
            <v-list v-else density="compact">
              <v-list-item
                v-for="action in pendingActions"
                :key="action.id"
                :to="action.link"
                rounded="lg"
                class="mb-2 pending-action-item"
                :class="{ 'urgent-action': action.priority === 'urgent' }"
              >
                <template #prepend>
                  <v-avatar
                    :color="action.color"
                    size="40"
                    variant="tonal"
                  >
                    <v-icon :icon="action.icon" />
                  </v-avatar>
                </template>
                
                <v-list-item-title class="font-weight-medium">
                  {{ action.title }}
                  <v-icon 
                    v-if="action.priority === 'urgent'"
                    size="small" 
                    color="error" 
                    class="ml-2"
                  >
                    mdi-alert
                  </v-icon>
                </v-list-item-title>
                <v-list-item-subtitle class="text-wrap">
                  {{ action.description }}
                  <div class="d-flex align-center mt-1">
                    <v-chip
                      size="x-small"
                      :color="getPriorityColor(action.priority)"
                      variant="outlined"
                      class="mr-2"
                    >
                      {{ action.priority.toUpperCase() }}
                    </v-chip>
                    <v-chip
                      v-if="action.type"
                      size="x-small"
                      color="grey"
                      variant="text"
                    >
                      {{ action.type }}
                    </v-chip>
                  </div>
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Recent Activity -->
    <v-row class="mt-6">
      <v-col cols="12">
        <v-card rounded="lg">
          <v-card-title class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="info">mdi-history</v-icon>
              Recent Activity
            </div>
            <v-btn
              variant="text"
              color="primary"
              size="small"
            >
              View All
            </v-btn>
          </v-card-title>
          
          <v-card-text>
            <v-timeline density="compact" side="end">
              <v-timeline-item
                v-for="activity in recentActivity"
                :key="activity.id"
                :dot-color="activity.color"
                size="small"
              >
                <template #icon>
                  <v-icon :icon="activity.icon" size="16" />
                </template>
                
                <v-card variant="outlined" rounded="lg">
                  <v-card-text class="py-3">
                    <p class="font-weight-medium mb-1">{{ activity.title }}</p>
                    <p class="text-body-2 text-medium-emphasis mb-2">
                      {{ activity.description }}
                    </p>
                    <p class="text-caption text-medium-emphasis">
                      {{ formatRelativeTime(activity.timestamp) }}
                    </p>
                  </v-card-text>
                </v-card>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/store/auth'
import moment from 'moment'
import api, { getFileUrl } from '@/services/api'

export default {
  name: 'Dashboard',
  setup() {
    const authStore = useAuthStore()
    
    const loading = ref(false)
    const stats = ref([
      {
        title: 'Total Meetings',
        value: '0',
        change: null,
        changeColor: 'text-medium-emphasis',
        changeIcon: null,
        color: 'primary',
        icon: 'mdi-calendar-account',
        loading: true
      },
      {
        title: 'Pending Votes',
        value: '0',
        change: null,
        changeColor: 'text-medium-emphasis',
        changeIcon: null,
        color: 'warning',
        icon: 'mdi-vote',
        loading: true
      },
      {
        title: 'Documents',
        value: '0',
        change: null,
        changeColor: 'text-medium-emphasis',
        changeIcon: null,
        color: 'info',
        icon: 'mdi-file-document',
        loading: true
      },
      {
        title: 'Resolutions',
        value: '0',
        change: null,
        changeColor: 'text-medium-emphasis',
        changeIcon: null,
        color: 'success',
        icon: 'mdi-gavel',
        loading: true
      }
    ])
    
    const upcomingMeetings = ref([
      {
        id: 1,
        title: 'Board Strategic Planning Session',
        start_datetime: '2025-11-08T10:00:00Z',
        meeting_type: { name: 'Strategic', color: 'primary' }
      },
      {
        id: 2,
        title: 'Finance Committee Review',
        start_datetime: '2025-11-10T14:00:00Z',
        meeting_type: { name: 'Committee', color: 'warning' }
      },
      {
        id: 3,
        title: 'Emergency Operations Meeting',
        start_datetime: '2025-11-12T09:00:00Z',
        meeting_type: { name: 'Emergency', color: 'error' }
      }
    ])
    
    const pendingActions = ref([])
    
    const recentActivity = ref([
      {
        id: 1,
        title: 'New meeting scheduled',
        description: 'Strategic Planning Session added for November 8th',
        timestamp: '2025-11-05T14:30:00Z',
        color: 'primary',
        icon: 'mdi-calendar-plus'
      },
      {
        id: 2,
        title: 'Resolution approved',
        description: 'Port Expansion Resolution #2025-003 was approved',
        timestamp: '2025-11-05T10:15:00Z',
        color: 'success',
        icon: 'mdi-check-circle'
      },
      {
        id: 3,
        title: 'Document uploaded',
        description: 'Financial Report Q3 2025.pdf was added',
        timestamp: '2025-11-04T16:45:00Z',
        color: 'info',
        icon: 'mdi-file-upload'
      },
      {
        id: 4,
        title: 'New board member',
        description: 'John Doe joined as Finance Committee member',
        timestamp: '2025-11-03T09:20:00Z',
        color: 'purple',
        icon: 'mdi-account-plus'
      }
    ])
    
    const user = computed(() => authStore.user || {})
    
    const getGreeting = () => {
      const hour = new Date().getHours()
      if (hour < 12) return 'Good morning!'
      if (hour < 17) return 'Good afternoon!'
      return 'Good evening!'
    }
    
    const formatDateTime = (dateString) => {
      return moment(dateString).format('MMM DD, YYYY [at] h:mm A')
    }
    
    const formatRelativeTime = (dateString) => {
      return moment(dateString).fromNow()
    }

    const getPriorityColor = (priority) => {
      switch (priority) {
        case 'urgent': return 'error'
        case 'high': return 'warning'
        case 'medium': return 'info'
        case 'low': return 'success'
        default: return 'grey'
      }
    }

    const calculateTrend = (current, previous) => {
      if (!previous || previous === 0) {
        return current > 0 ? { change: 'New', color: 'text-info', icon: 'mdi-plus' } : null
      }
      
      const percentChange = Math.round(((current - previous) / previous) * 100)
      if (percentChange === 0) return null
      
      return {
        change: `${percentChange > 0 ? '+' : ''}${percentChange}%`,
        color: percentChange > 0 ? 'text-success' : 'text-error',
        icon: percentChange > 0 ? 'mdi-trending-up' : 'mdi-trending-down'
      }
    }
    
    const loadDashboardData = async () => {
      loading.value = true
      try {
        // Load statistics in parallel
        await Promise.allSettled([
          loadStats(),
          loadPendingActions(),
          loadUpcomingMeetings(),
          loadRecentActivity()
        ])
      } catch (error) {
        console.error('Failed to load dashboard data:', error)
      } finally {
        loading.value = false
      }
    }

    const loadStats = async () => {
      const currentUserId = authStore.user?.id
      
      try {
        // Load meetings count
        const meetingsResponse = await api.get('/meetings?per_page=1')
        const totalMeetings = meetingsResponse.data.pagination?.total || 0
        
        // Get meetings from this month vs last month for trend
        const thisMonth = new Date()
        const lastMonth = new Date()
        lastMonth.setMonth(lastMonth.getMonth() - 1)
        
        const thisMonthStart = new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 1).toISOString().split('T')[0]
        const lastMonthStart = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1).toISOString().split('T')[0]
        const lastMonthEnd = new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 0).toISOString().split('T')[0]
        
        try {
          const [thisMonthResponse, lastMonthResponse] = await Promise.all([
            api.get(`/meetings?start_date=${thisMonthStart}&per_page=1`),
            api.get(`/meetings?start_date=${lastMonthStart}&end_date=${lastMonthEnd}&per_page=1`)
          ])
          
          const thisMonthCount = thisMonthResponse.data.pagination?.total || 0
          const lastMonthCount = lastMonthResponse.data.pagination?.total || 0
          const trend = calculateTrend(thisMonthCount, lastMonthCount)
          
          const meetingsStat = stats.value.find(s => s.title === 'Total Meetings')
          if (meetingsStat) {
            meetingsStat.value = totalMeetings.toString()
            meetingsStat.loading = false
            if (trend) {
              meetingsStat.change = trend.change
              meetingsStat.changeColor = trend.color
              meetingsStat.changeIcon = trend.icon
            }
          }
        } catch {
          // Fallback to just total count
          const meetingsStat = stats.value.find(s => s.title === 'Total Meetings')
          if (meetingsStat) {
            meetingsStat.value = totalMeetings.toString()
            meetingsStat.loading = false
          }
        }
      } catch (error) {
        console.warn('Failed to load meetings count:', error)
        const meetingsStat = stats.value.find(s => s.title === 'Total Meetings')
        if (meetingsStat) meetingsStat.loading = false
      }

      try {
        // Load pending votes for current user
        const resolutionsResponse = await api.get('/resolutions?status=voting')
        const votingResolutions = resolutionsResponse.data.resolutions || []
        
        const pendingVotesCount = votingResolutions.filter(resolution => {
          if (!resolution.votes || !currentUserId) return true
          return !resolution.votes.some(vote => vote.user_id === currentUserId)
        }).length

        // Update pending votes stat
        const votesStat = stats.value.find(s => s.title === 'Pending Votes')
        if (votesStat) {
          votesStat.value = pendingVotesCount.toString()
          votesStat.loading = false
          // Set color based on urgency
          if (pendingVotesCount > 0) {
            votesStat.color = 'warning'
            votesStat.changeColor = 'text-warning'
            votesStat.change = 'Action needed'
          } else {
            votesStat.color = 'success'
            votesStat.changeColor = 'text-success'
            votesStat.change = 'All up to date'
          }
        }
      } catch (error) {
        console.warn('Failed to load pending votes:', error)
        const votesStat = stats.value.find(s => s.title === 'Pending Votes')
        if (votesStat) votesStat.loading = false
      }

      try {
        // Load documents count
        const documentsResponse = await api.get('/documents?per_page=1')
        const totalDocuments = documentsResponse.data.total || 0
        
        // Update documents stat
        const documentsStat = stats.value.find(s => s.title === 'Documents')
        if (documentsStat) {
          documentsStat.value = totalDocuments.toString()
          documentsStat.loading = false
          
          // Add contextual info based on document count
          if (totalDocuments > 0) {
            documentsStat.change = 'Available'
            documentsStat.changeColor = 'text-success'
            documentsStat.changeIcon = 'mdi-check'
          }
        }
      } catch (error) {
        console.warn('Failed to load documents count:', error)
        const documentsStat = stats.value.find(s => s.title === 'Documents')
        if (documentsStat) documentsStat.loading = false
      }

      try {
        // Load resolutions count and status breakdown
        const resolutionsResponse = await api.get('/resolutions?per_page=1')
        const totalResolutions = resolutionsResponse.data.pagination?.total || 0
        
        // Get approved resolutions for positive indicator
        try {
          const approvedResponse = await api.get('/resolutions?status=approved&per_page=1')
          const approvedCount = approvedResponse.data.pagination?.total || 0
          const approvalRate = totalResolutions > 0 ? Math.round((approvedCount / totalResolutions) * 100) : 0
          
          const resolutionsStat = stats.value.find(s => s.title === 'Resolutions')
          if (resolutionsStat) {
            resolutionsStat.value = totalResolutions.toString()
            resolutionsStat.loading = false
            
            if (approvalRate >= 70) {
              resolutionsStat.change = `${approvalRate}% approved`
              resolutionsStat.changeColor = 'text-success'
              resolutionsStat.changeIcon = 'mdi-trending-up'
            } else if (approvalRate > 0) {
              resolutionsStat.change = `${approvalRate}% approved`
              resolutionsStat.changeColor = 'text-warning'
              resolutionsStat.changeIcon = 'mdi-minus'
            } else if (totalResolutions > 0) {
              resolutionsStat.change = 'In progress'
              resolutionsStat.changeColor = 'text-info'
              resolutionsStat.changeIcon = 'mdi-clock'
            }
          }
        } catch {
          // Fallback to basic count
          const resolutionsStat = stats.value.find(s => s.title === 'Resolutions')
          if (resolutionsStat) {
            resolutionsStat.value = totalResolutions.toString()
            resolutionsStat.loading = false
          }
        }
      } catch (error) {
        console.warn('Failed to load resolutions count:', error)
        const resolutionsStat = stats.value.find(s => s.title === 'Resolutions')
        if (resolutionsStat) resolutionsStat.loading = false
      }
    }

    const loadUpcomingMeetings = async () => {
      try {
        // Get meetings in next 30 days
        const today = new Date().toISOString().split('T')[0]
        const nextMonth = new Date()
        nextMonth.setDate(nextMonth.getDate() + 30)
        const endDate = nextMonth.toISOString().split('T')[0]
        
        const response = await api.get(`/meetings?status=scheduled&start_date=${today}&end_date=${endDate}`)
        upcomingMeetings.value = (response.data.meetings || []).slice(0, 3) // Show top 3
      } catch (error) {
        console.warn('Failed to load upcoming meetings:', error)
        upcomingMeetings.value = []
      }
    }

    const loadRecentActivity = async () => {
      try {
        // This would be better with a dedicated activity/audit log endpoint
        // For now, we'll simulate recent activity from various endpoints
        const activities = []
        
        // Get recent meetings
        const meetingsResponse = await api.get('/meetings?per_page=3')
        const recentMeetings = meetingsResponse.data.meetings || []
        
        recentMeetings.forEach(meeting => {
          activities.push({
            id: `meeting-${meeting.id}`,
            title: 'Meeting scheduled',
            description: `${meeting.title} scheduled for ${formatDateTime(meeting.start_datetime)}`,
            timestamp: meeting.created_at,
            color: 'primary',
            icon: 'mdi-calendar-plus'
          })
        })

        // Get recent resolutions
        const resolutionsResponse = await api.get('/resolutions?per_page=2')
        const recentResolutions = resolutionsResponse.data.resolutions || []
        
        recentResolutions.forEach(resolution => {
          activities.push({
            id: `resolution-${resolution.id}`,
            title: `Resolution ${resolution.status}`,
            description: `${resolution.title} - ${resolution.status}`,
            timestamp: resolution.updated_at,
            color: resolution.status === 'approved' ? 'success' : 
                   resolution.status === 'voting' ? 'warning' : 'info',
            icon: resolution.status === 'approved' ? 'mdi-check-circle' : 'mdi-gavel'
          })
        })

        // Sort by timestamp and take most recent
        activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        recentActivity.value = activities.slice(0, 4)
        
      } catch (error) {
        console.warn('Failed to load recent activity:', error)
        recentActivity.value = []
      }
    }

    const loadPendingActions = async () => {
      try {
        const currentUserId = authStore.user?.id
        if (!currentUserId) {
          pendingActions.value = []
          return
        }

        const actions = []

        // 1. Get resolutions that need user's vote
        try {
          const resolutionsResponse = await api.get('/resolutions?status=voting')
          const votingResolutions = resolutionsResponse.data.resolutions || []
          
          // Filter resolutions where current user hasn't voted yet
          const pendingVotes = votingResolutions.filter(resolution => {
            if (!resolution.votes) return true
            return !resolution.votes.some(vote => vote.user_id === currentUserId)
          })

          // Add voting actions
          pendingVotes.forEach(resolution => {
            const daysLeft = resolution.voting_deadline ? 
              Math.ceil((new Date(resolution.voting_deadline) - new Date()) / (1000 * 60 * 60 * 24)) : null
            
            actions.push({
              id: `vote-${resolution.id}`,
              title: `Vote Required: ${resolution.title}`,
              description: daysLeft && daysLeft > 0 ? 
                `Priority: ${resolution.priority || 'Medium'} • ${resolution.description.substring(0, 40)}... • ${daysLeft} days left` :
                `Priority: ${resolution.priority || 'Medium'} • ${resolution.description.substring(0, 60)}...`,
              link: `/resolutions/${resolution.id}`,
              color: resolution.priority === 'Urgent' || (daysLeft && daysLeft <= 2) ? 'error' : 
                     resolution.priority === 'High' ? 'warning' : 'info',
              icon: 'mdi-vote',
              priority: resolution.priority === 'Urgent' || (daysLeft && daysLeft <= 2) ? 'urgent' : 
                       resolution.priority === 'High' ? 'high' : 'normal',
              resolutionPriority: resolution.priority || 'Medium',
              type: 'voting',
              deadline: resolution.voting_deadline
            })
          })
        } catch (error) {
          console.warn('Failed to load voting resolutions:', error)
        }

        // 2. Get upcoming meetings where user hasn't confirmed attendance
        try {
          const meetingsResponse = await api.get('/meetings?status=scheduled')
          const upcomingMeetings = meetingsResponse.data.meetings || []
          
          upcomingMeetings.forEach(meeting => {
            // Check if meeting is within next 7 days
            const meetingDate = new Date(meeting.start_datetime)
            const daysUntil = Math.ceil((meetingDate - new Date()) / (1000 * 60 * 60 * 24))
            
            if (daysUntil > 0 && daysUntil <= 7) {
              // Check if user has confirmed attendance
              const userAttendance = meeting.attendance?.find(att => att.user_id === currentUserId)
              
              if (!userAttendance || userAttendance.status === 'invited') {
                actions.push({
                  id: `attendance-${meeting.id}`,
                  title: `Confirm Attendance: ${meeting.title}`,
                  description: `Meeting scheduled for ${formatDateTime(meeting.start_datetime)}`,
                  link: `/meetings/${meeting.id}`,
                  color: daysUntil <= 1 ? 'error' : 'info',
                  icon: 'mdi-calendar-check',
                  priority: daysUntil <= 1 ? 'urgent' : 'medium',
                  type: 'attendance',
                  deadline: meeting.start_datetime
                })
              }
            }
          })
        } catch (error) {
          console.warn('Failed to load meetings for attendance:', error)
        }

        // 3. Check for profile completeness (if user profile is incomplete)
        if (authStore.user && (!authStore.user.phone || !authStore.user.profile_image)) {
          actions.push({
            id: 'profile-incomplete',
            title: 'Complete Your Profile',
            description: 'Add missing information to your board member profile',
            link: '/profile',
            color: 'info',
            icon: 'mdi-account-edit',
            priority: 'low',
            type: 'profile'
          })
        }

        // Sort actions by priority and deadline
        const priorityOrder = { urgent: 1, high: 2, medium: 3, low: 4 }
        actions.sort((a, b) => {
          // First sort by priority
          const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority]
          if (priorityDiff !== 0) return priorityDiff
          
          // Then by deadline if available
          if (a.deadline && b.deadline) {
            return new Date(a.deadline) - new Date(b.deadline)
          }
          if (a.deadline) return -1
          if (b.deadline) return 1
          
          return 0
        })

        pendingActions.value = actions.slice(0, 5) // Show max 5 actions

      } catch (error) {
        console.error('Failed to load pending actions:', error)
        pendingActions.value = []
      }
    }
    
    onMounted(() => {
      loadDashboardData()
    })
    
    return {
      loading,
      stats,
      upcomingMeetings,
      pendingActions,
      recentActivity,
      user,
      getGreeting,
      formatDateTime,
      formatRelativeTime,
      getPriorityColor,
      calculateTrend,
      getFileUrl
    }
  }
}
</script>

<style scoped lang="scss">
.welcome-card {
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, rgba(var(--v-theme-primary), 0.8) 100%);
}

.stat-card {
  transition: transform 0.2s ease-in-out;
  
  &:hover {
    transform: translateY(-2px);
  }
}

.v-timeline-item {
  padding-bottom: 16px !important;
}

.v-card {
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
  }
}

.v-list-item {
  border: 1px solid transparent;
  margin-bottom: 8px;
  
  &:hover {
    border-color: rgba(var(--v-theme-primary), 0.2);
    background: rgba(var(--v-theme-primary), 0.05);
  }
}

.pending-action-item {
  &.urgent-action {
    border-color: rgba(var(--v-theme-error), 0.3) !important;
    background: rgba(var(--v-theme-error), 0.05) !important;
    
    &:hover {
      border-color: rgba(var(--v-theme-error), 0.5) !important;
      background: rgba(var(--v-theme-error), 0.1) !important;
    }
  }
}

.text-wrap {
  white-space: normal !important;
}
</style>